import { Card, Loaded, Notice, Page } from '../components/ui'
import { get } from '../lib/api'
import { titleCase } from '../lib/format'
import { useResource } from '../lib/useResource'

type Matrix = Record<string, string[]>

const GROUPS: { eyebrow: string; prefix: string }[] = [
  { eyebrow: 'Self-service', prefix: 'self:' },
  { eyebrow: 'Other users', prefix: 'user:' },
  { eyebrow: 'Health data', prefix: 'phi:' },
  { eyebrow: 'Emergency', prefix: 'emergency:' },
  { eyebrow: 'AI', prefix: 'ai:' },
  { eyebrow: 'Governance', prefix: 'privacy:' },
  { eyebrow: 'Governance', prefix: 'audit:' },
  { eyebrow: 'Commerce & config', prefix: 'billing:' },
  { eyebrow: 'Commerce & config', prefix: 'config:' },
  { eyebrow: 'Commerce & config', prefix: 'role:' },
  { eyebrow: 'Commerce & config', prefix: 'admin:' },
]

function groupFor(permission: string): string {
  return GROUPS.find((g) => permission.startsWith(g.prefix))?.eyebrow ?? 'Other'
}

export function Roles() {
  const matrix = useResource((signal) => get<Matrix>('/v1/admin/roles', undefined, signal))

  return (
    <Page
      title="Roles & permissions"
      subtitle="The mapping the API actually enforces, read live from app/core/rbac.py. There is no second copy of this table in the console."
    >
      <Notice>
        Roles are defined in code, not in the database. Adding one is a reviewed change to{' '}
        <span className="mono">ROLE_PERMISSIONS</span> — which is what makes “who can read PHI?”
        answerable by reading a single file rather than by querying production. Custom roles
        assembled at runtime are deliberately not supported.
      </Notice>

      <Loaded resource={matrix}>
        {(data) => {
          const roles = Object.keys(data).sort()
          const permissions = [...new Set(Object.values(data).flat())].sort()
          const grouped = new Map<string, string[]>()
          for (const permission of permissions) {
            const key = groupFor(permission)
            grouped.set(key, [...(grouped.get(key) ?? []), permission])
          }

          return (
            <Card flush>
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th style={{ minWidth: 190 }}>Permission</th>
                      {roles.map((role) => (
                        <th key={role} style={{ textAlign: 'center' }}>
                          {titleCase(role)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[...grouped.entries()].map(([group, items]) => (
                      <>
                        <tr key={group}>
                          <td
                            colSpan={roles.length + 1}
                            style={{ background: 'var(--surface-alt)', padding: '8px 16px' }}
                          >
                            <span className="eyebrow">{group}</span>
                          </td>
                        </tr>
                        {items.map((permission) => (
                          <tr key={permission}>
                            <td className="strong mono">{permission}</td>
                            {roles.map((role) => {
                              const holds = data[role]?.includes(permission) ?? false
                              const sensitive =
                                permission.startsWith('phi:') ||
                                permission === 'ai:prompt:read' ||
                                permission === 'role:write'
                              return (
                                <td key={role} style={{ textAlign: 'center' }}>
                                  {holds ? (
                                    <span
                                      style={{
                                        color: sensitive ? 'var(--alert-bright)' : 'var(--green)',
                                        fontWeight: 700,
                                      }}
                                      title={sensitive ? 'Sensitive permission' : undefined}
                                    >
                                      ●
                                    </span>
                                  ) : (
                                    <span className="dim">·</span>
                                  )}
                                </td>
                              )
                            })}
                          </tr>
                        ))}
                      </>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )
        }}
      </Loaded>

      <Card title="Deliberate exclusions" hint="Absences that are load-bearing">
        <ul className="stack" style={{ margin: 0, paddingLeft: 18, gap: 8 }}>
          <li>
            <strong>Researcher</strong> never holds <span className="mono">phi:read:any</span>.
            De-identification is enforced by the permission set, not by a policy document a
            researcher agrees to.
          </li>
          <li>
            <strong>Support</strong> holds neither PHI read nor billing write. A support agent can
            see that an account exists, suspend it, and read its billing state — nothing else.
          </li>
          <li>
            <strong>No default role</strong> holds <span className="mono">ai:prompt:read</span>,
            including admin. Reading what a patient typed to the co-pilot is a PHI event and needs
            an explicit, separate grant.
          </li>
        </ul>
      </Card>
    </Page>
  )
}
